import React from "react";
import { motion } from "framer-motion";
import { Heart, ShieldCheck, Clock3, Truck } from "lucide-react";
import { ASSETS } from "@/lib/site-data";

const VALUES = [
  {
    icon: Heart,
    title: "Family-run, neighbor-friendly",
    text: "We live and work in Tallahassee. Your yard isn't a job site — it's your home.",
  },
  {
    icon: Clock3,
    title: "On time, every time",
    text: "When we say Tuesday, we mean Tuesday. The same crew, the same care, week after week.",
  },
  {
    icon: ShieldCheck,
    title: "Licensed & insured",
    text: "Full liability coverage on every property we step onto. No worries — just results.",
  },
  {
    icon: Truck,
    title: "Pro equipment, pro finish",
    text: "Commercial mowers, sharp edges, and the right tool for every shrub, branch, and bed.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-3xl overflow-hidden border-4 border-white shadow-[0_30px_60px_-25px_rgba(26,46,32,0.5)] aspect-[4/5]">
            <img
              src={ASSETS.portfolioBefore}
              alt="Imperial Landscaping, TLH crew at work in Tallahassee"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#1A2E20]/80 to-transparent" />
            <div className="absolute left-5 right-5 bottom-5 text-white">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#EAB308]">
                On the job
              </div>
              <div className="font-['Outfit'] text-xl md:text-2xl font-bold tracking-tight mt-1">
                A Tallahassee crew, ready to roll.
              </div>
            </div>
          </div>
          <div className="hidden md:block absolute -top-6 -right-6 bg-[#EAB308] rounded-2xl px-6 py-5 shadow-[0_18px_40px_-15px_rgba(234,179,8,0.85)] rotate-[3deg]">
            <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#422006]">
              Since day one
            </div>
            <div className="font-['Outfit'] text-2xl lg:text-3xl font-black text-[#422006] tracking-tighter">
              Local. Loud. Loyal.
            </div>
          </div>
        </motion.div>

        <div className="lg:col-span-7">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="text-xs tracking-[0.28em] uppercase font-bold text-[#2E5C3E] mb-4"
          >
            About Imperial Landscaping, TLH
          </motion.div>
          <motion.h2
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            custom={1}
            className="font-['Outfit'] text-4xl md:text-6xl font-black tracking-tighter text-[#1A2E20] leading-[1.02]"
          >
            We treat every yard like it's the one we drive past every day.
          </motion.h2>
          <motion.p
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            custom={2}
            className="mt-6 text-base md:text-lg text-[#4A6E55] leading-relaxed max-w-xl"
          >
            Imperial Landscaping, TLH was started by people who actually love being
            outside. Florida summers, sandy soil, sudden rain — we know the
            quirks of growing things in Tallahassee, and we keep your property
            looking sharp through every season.
          </motion.p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.title}
                  data-testid={`value-card-${i}`}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-60px" }}
                  variants={fadeUp}
                  custom={i + 1}
                  className="rounded-2xl bg-white border border-[#D1DED5] p-6 lift-on-hover"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#E5EDE7] text-[#2E5C3E] grid place-items-center mb-4">
                    <Icon className="w-5 h-5" strokeWidth={2.2} />
                  </div>
                  <div className="font-['Outfit'] font-bold text-lg text-[#1A2E20]">
                    {v.title}
                  </div>
                  <p className="mt-1.5 text-sm text-[#4A6E55] leading-relaxed">
                    {v.text}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
